/**
 * M7 S32: one log, as found: title, date, source, body and its table (form carries the fact, spec §2.1). The same
 * table form serves Your records (`LogTable`).
 */
import type { ReactNode } from 'react'
import type { LogsConfig } from './models/config'
import type { LogEntryView, LogTable } from './models/logs'

export function Table(p: { table: LogTable; testId?: string }): ReactNode {
  return (
    <table className="lg-table" data-testid={p.testId}>
      <thead>
        <tr>
          {p.table.columns.map((c, i) => (
            <th key={i}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {p.table.rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export interface LogViewProps {
  entry: LogEntryView
  config: LogsConfig
  focused: boolean
  onBack: () => void
}

export function LogView(p: LogViewProps): ReactNode {
  const e = p.entry
  return (
    <article className="lg-log" data-testid="logs-log" data-log={e.id}>
      <div className="lg-log__head">
        <h3 className="lg-log__title">{e.title}</h3>
        {e.date === '' ? null : <span className="lg-row__meta">{e.date}</span>}
        <span className="lg-row__meta">{e.sourceLabel}</span>
      </div>
      {e.body === '' ? null : <p className="lg-log__body">{e.body}</p>}
      {e.table === null ? null : <Table table={e.table} />}
      <button
        type="button"
        tabIndex={-1}
        className="sui-btn lg-btn"
        data-testid="logs-back"
        {...(p.focused ? { 'data-focused': '' } : {})}
        onClick={p.onBack}
      >
        {p.config.back}
      </button>
    </article>
  )
}

export interface EntryListProps {
  entries: readonly LogEntryView[]
  config: LogsConfig
  /** The focused row, or -1. */
  focusedRow: number
  onOpen: (index: number) => void
}

/** A tab's entries: title, date, an unread mark (a word, never a count). */
export function EntryList(p: EntryListProps): ReactNode {
  if (p.entries.length === 0) return <p className="sui-empty">{p.config.empty.source}</p>
  return (
    <ul className="lg-list">
      {p.entries.map((e, i) => (
        <li
          key={e.id}
          className="lg-row lg-row--entry"
          data-testid="logs-entry"
          data-log={e.id}
          data-unread={e.unread ? '' : undefined}
          {...(p.focusedRow === i ? { 'data-focused': '' } : {})}
          onClick={() => p.onOpen(i)}
        >
          <span className="lg-row__title">{e.title}</span>
          {e.date === '' ? null : <span className="lg-row__meta">{e.date}</span>}
          {e.unread ? <span className="lg-mark">{p.config.unread}</span> : null}
        </li>
      ))}
    </ul>
  )
}
